import { useEffect, useRef, useState } from "react";
import ReactQuill, { ReactQuillProps, Quill } from "react-quill";
import { Styles } from "./styled";
import AutoLinks from "quill-auto-links";
import { toNumber } from "lodash";
const Delta = Quill.import("delta");

Quill.register("modules/autoLinks", AutoLinks);

interface IProps extends ReactQuillProps {
  quillRef?: React.MutableRefObject<ReactQuill | undefined>;
  hiddenToolbar?: boolean;
}

const ReactQuillCustomJP = ({ quillRef, hiddenToolbar, ...props }: IProps) => {
  const ref = useRef<ReactQuill>(null);
  const refSelection = useRef<{ index: number; length: number }>();

  const [shouldShowToolTipPreview, setShouldShowToolTipPreview] =
    useState(false);
  const [textLink, setTextLink] = useState("");

  // Sync quillRef
  if (quillRef && ref.current) {
    quillRef.current = ref.current;
  }

  const handleSetShowTooltipPreview = () => {
    const editor = ref.current?.editor;
    if (editor) {
      const selection = editor.getSelection();
      if (selection) {
        refSelection.current = { ...selection };
        const [leaf] = editor.getLeaf(selection.index);
        const domNode = leaf?.domNode;
        const anchor =
          domNode?.nodeType === 3
            ? domNode.parentElement?.closest("a")
            : domNode?.closest?.("a");

        if (anchor) {
          const { innerText } = anchor;
          setTextLink(innerText);
          const isURL = /^https?:\/\/[^\s]+$/.test(innerText);
          setShouldShowToolTipPreview(!isURL);
        } else {
          const textSelection =
            editor?.getText(selection.index, selection.length) || "";
          setTextLink(textSelection);
        }
      }
    }
  };

  const addCustomLink = (url: string, label: string) => {
    const quill = ref?.current?.getEditor();
    const range = refSelection?.current;

    if (range) {
      // Delete the selected text
      quill?.deleteText(range.index, range.length);

      // Insert the new label
      quill?.insertText(range.index, label, "link", url);

      // Move cursor after inserted label
      quill?.setSelection(range.index + label.length, 0);
    }
  };
  const editingArea = ref.current?.editingArea as HTMLDivElement;
  if (editingArea) {
    const qlEditing = editingArea.querySelector(
      ".ql-tooltip",
    ) as HTMLDivElement;

    setTimeout(() => {
      if (!qlEditing) return;
      const left = toNumber(qlEditing.style.left.replace("px", ""));
      if (left < 2) qlEditing.style.left = "1px";
    }, 20);
  }

  useEffect(() => {
    const editingArea = ref.current?.editingArea as HTMLDivElement;

    const editor = ref.current?.editor;
    if (editingArea && editor) {
      const qlEditing = editingArea.querySelector(
        ".ql-tooltip",
      ) as HTMLDivElement;

      if (!qlEditing) return () => {};

      // Clean up previously injected elements
      qlEditing.querySelector(".add-name-url-container")?.remove();
      qlEditing.querySelector(".label-url")?.remove();
      qlEditing.querySelector(".button-save-link")?.remove();

      // Create container
      const newDiv = document.createElement("div");
      newDiv.classList.add("add-name-url-container");

      // Label
      const spanLabelURL = document.createElement("span");
      spanLabelURL.textContent = "URL";
      spanLabelURL.classList.add("label-url");

      // Input
      const inputNameLink = document.createElement("input");
      inputNameLink.type = "text";
      inputNameLink.value = textLink;

      // Display label
      const span = document.createElement("span");
      span.textContent = "リンク";
      span.classList.add("label-name-url");

      // Save button
      const buttonSave = document.createElement("span");
      buttonSave.textContent = "保存";
      buttonSave.classList.add("button-save-link");
      buttonSave.style.cursor = "pointer";

      // Append elements
      newDiv.appendChild(span);
      newDiv.appendChild(inputNameLink);
      const firstEle = qlEditing.querySelector("a");
      qlEditing.insertBefore(newDiv, firstEle);
      qlEditing.insertBefore(spanLabelURL, firstEle);
      qlEditing.appendChild(buttonSave);

      // Save handler
      buttonSave.onclick = (e) => {
        if (!qlEditing.classList.contains("ql-editing")) return;
        e.preventDefault();
        const tooltip = (editor as any)?.theme?.tooltip;
        const linkRange = tooltip?.linkRange;
        const inputURLs = qlEditing.querySelectorAll(
          `.ql-tooltip.ql-editing > input[type=text]`,
        );

        const inputURL = (inputURLs[1] || inputURLs[0]) as HTMLInputElement;
        const newText = inputNameLink.value;

        if (!linkRange) {
          return addCustomLink(inputURL.value, newText);
        }

        const delta = new Delta()
          .retain(linkRange.index)
          .delete(linkRange.length)
          .insert(newText, { link: inputURL.value });

        editor.updateContents(delta);
        editor.setSelection(linkRange.index + newText.length, 0);
        tooltip.hide();
      };

      // Cleanup on unmount
      return () => {
        qlEditing.querySelector(".add-name-url-container")?.remove();
        qlEditing.querySelector(".label-url")?.remove();
        qlEditing.querySelector(".button-save-link")?.remove();
      };
    }
  }, [textLink]);

  return (
    <Styles
      className={`${shouldShowToolTipPreview ? "show-tooltip-preview" : ""} ${
        hiddenToolbar ? "hidden-toolbar" : ""
      }`}
    >
      <div
        style={{
          position: "relative",
          height: "100%",
        }}
      >
        {props.readOnly && (
          <div
            className="toolbar_read_only"
            style={{
              height: "30px",
              width: "100%",
              position: "absolute",
              zIndex: 100,
              cursor: "not-allowed",
            }}
          />
        )}
        <ReactQuill
          {...props}
          modules={{
            ...props.modules,
            autoLinks: true,
          }}
          ref={ref}
          scrollingContainer="html"
          onChangeSelection={(...params) => {
            props.onChangeSelection?.(...params);
            handleSetShowTooltipPreview();
          }}
        />
      </div>
    </Styles>
  );
};

export default ReactQuillCustomJP;
